import { Component, useContext } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Button } from 'reactstrap';
import { Form } from 'react-bootstrap';
import { AuthenticationContext } from '../../../msal/AuthenticationContext';
import { NotificationContext } from '../../../context/NotificationContext';
import { TenantContext } from '../../../context/TenantContext';
import * as apiCalls from '../../../logic/apiCalls';
import { DeleteButton } from '../../../components/DeleteButton';
import { ButtonWithDisabledTooltip } from '../../../components/ButtonWithDisabledTooltip';
import styles from './ModalUserTokensUpdate.module.scss';

class ModalUserTokensUpdateInternal extends Component {
    state = {
        isLoading: false,
        tokens: [],
        fcmTokenToAdd: "",
        deviceNameToAdd: "",
    }

    constructor(props) {
        super(props);

        // Build the description validation Regex.
        let nonPrintableCharStart = String.fromCharCode(0x00); // NULL char
        let nonPrintableCharEnd = String.fromCharCode(0x1F); // SPACE char
        let nonPrintableCharWithSpaceEnd = String.fromCharCode(0x20); // SPACE char
        let delRegex = String.fromCharCode(0x7F); // DEL char
        let backslashRegex = "\\\\"; // The parsing of the literal string will reduce this to 2 backslashes, the parsing of this string by the RegExp class will reduce it to a single literal backslash to be matched.
        let forwardslashRegex = "\\/"; 
        let bracketOpeningRegex = "\\["; 
        let bracketClosingRegex = "\\]"; 
        let quotationRegex = "\\\"";
        
        this.tokenValidationRegex = new RegExp(`^[^${nonPrintableCharStart}-${nonPrintableCharWithSpaceEnd}${delRegex}${backslashRegex}${forwardslashRegex}${bracketOpeningRegex}${bracketClosingRegex}${quotationRegex}]*$`);
        this.deviceNameValidationRegex = new RegExp(`^[^${nonPrintableCharStart}-${nonPrintableCharEnd}${delRegex}${backslashRegex}${forwardslashRegex}${bracketOpeningRegex}${bracketClosingRegex}${quotationRegex}]*$`);
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.isOpen && this.props.isOpen) {
            this.setInitialValues();
        }
    }

    setInitialValues = () => {
        this.setState(
            {
                isLoading: true,
                tokens: [],
                fcmTokenToAdd: "",
                deviceNameToAdd: "",
            }
        );
        this.getTokens();
    }

    getTokens = async () => {
        try {
            console.log("[getTokens] Invoking the back-end API to get all tokens of this user.");

            const tokens = await apiCalls.getTokens(this.props.authenticationContext, this.props.tenantContext.tenantId, this.props.userToUpdate.userId);

            this.setState({
                tokens: tokens,
            });               
        }
        catch (err) {
            console.log(`[getTokens] Error retrieving the tokens: ${err}`);
            this.props.notificationContext.setCommunicationFailureNotification();               
        }
        finally {
            this.setState({
                isLoading: false,
            });
        }
    }

    handleTokenAdd = async () => {
        const fcmTokenToAdd = this.state.fcmTokenToAdd.trim();
        const deviceNameToAdd = this.state.deviceNameToAdd.trim();

        const tokens = await apiCalls.addToken(this.props.authenticationContext, this.props.tenantContext.tenantId, this.props.userToUpdate.userId, fcmTokenToAdd, deviceNameToAdd);

        this.setState({
            fcmTokenToAdd: "",
            deviceNameToAdd: "",
        });

        this.props.notificationContext.setNotification("Adding token.", `Token '${fcmTokenToAdd.substring(0, 3)} ●●●●●●●●●●' for device '${deviceNameToAdd}' successfully added to user '${this.props.userToUpdate.name}'.`, 'success');

        this.getTokens();
    }

    handleTokenDelete = async (token) => {
        await apiCalls.deleteToken(this.props.authenticationContext, this.props.tenantContext.tenantId, this.props.userToUpdate.userId, token.tokenId);

        this.props.notificationContext.setNotification("Deleting token.", `Token '${token.fcmTokenPrefix} ●●●●●●●●●●' for device '${token.deviceName}' successfully deleted from user '${this.props.userToUpdate.name}'.`, 'success');

        this.getTokens();
    }

    handleCloseButtonClicked = () => {
        this.props.onHide();
    }

    handleFcmTokenToAddChanged = (e) => {
        const updatedToken = e.target.value;

        if (updatedToken.length > 0 && !this.tokenValidationRegex.test(updatedToken)) {
            return;
        }
                
        this.setState({
            fcmTokenToAdd: updatedToken
        });
    }

    handleDeviceNameChanged = (e) => {
        const updatedDeviceName = e.target.value;

        if (updatedDeviceName.length > 0 && !this.deviceNameValidationRegex.test(updatedDeviceName)) {
            return;
        }
                
        this.setState({
            deviceNameToAdd: updatedDeviceName
        });
    }

    shouldAddButtonBeEnabled = () => {
        return this.getAddButtonTooltip() === null;
    }

    getAddButtonTooltip = () => {
        if (!this.props.isOpen) {
            return null;
        }

        if (this.state.fcmTokenToAdd === '') {
            return "Token may not be empty";
        }

        if (this.state.fcmTokenToAdd.length > 256) {
            return "Token must be less than 256 characters";
        }

        if (!this.tokenValidationRegex.test(this.state.fcmTokenToAdd)) {
            return "Token contains invalid characters";
        }

        if (this.state.deviceNameToAdd.length > 256) {
            return "Device name must be less than 256 characters";
        }

        if (!this.deviceNameValidationRegex.test(this.state.deviceNameToAdd)) {
            return "Device name contains invalid characters";
        }

        return null;
    }

    render() {
        const doneButtonText = this.props.isUpdate ? 'Update' : 'Add';

        return <Modal show={this.props.isOpen} onHide={this.props.onHide}>
            <Modal.Header closeButton>
                <Modal.Title>Firebase Tokens</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form className="mt-3" onSubmit={e => e.preventDefault()}>
                    <h5>
                        Current Tokens
                    </h5>
                    {this.state.isLoading ? (
                        <div>Loading...</div>
                    ) : (
                        <table className="table table-striped table-bordered my-4">
                            <thead className="thead-dark">
                                <tr key="modalTokenHeader">
                                    <th scope="col">Token</th>
                                    <th scope="col">Device Name</th>
                                    <th scope="col" width="70px">Delete</th>
                                </tr>
                            </thead>
                            <tbody>
                                {this.state.tokens.map((token) => {
                                    return <tr key={token.tokenId}>
                                        <td className="align-middle">
                                            {token.fcmTokenPrefix} ●●●●●●●●●●
                                        </td>
                                        <td className="align-middle">
                                            {token.deviceName}
                                        </td>
                                        <td className="align-middle">
                                            <DeleteButton tooltip="Delete token" onClick={() => this.handleTokenDelete(token)} />
                                        </td>
                                    </tr>
                                })}
                            </tbody>
                        </table>
                    )}
                    <Form.Group controlId="newUserTokens" className="d-flex align-items-stretch">
                        <div className="flex-grow-1 me-2">
                            <Form.Control type="text" placeholder="Enter token" value={this.state.fcmTokenToAdd} autoComplete="off" onChange={this.handleFcmTokenToAddChanged} className="mb-2" />
                            <Form.Control type="text" placeholder="Enter device name" value={this.state.deviceNameToAdd} autoComplete="off" onChange={this.handleDeviceNameChanged} />
                        </div>
                        <div className={styles.addTokenButtonWrapper}>
                            <ButtonWithDisabledTooltip color='primary' size='sm' disabled={!this.shouldAddButtonBeEnabled()} disabledTooltip={this.getAddButtonTooltip()} onClick={this.handleTokenAdd} className="d-flex align-items-center justify-content-center h-100 w-100">
                                Add Token
                            </ButtonWithDisabledTooltip>
                        </div>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button color="primary" onClick={this.handleCloseButtonClicked}>Close</Button>{' '}
            </Modal.Footer>
        </Modal>;
    }
}

export const ModalUserTokensUpdate = (props) => {
    const authenticationContext = useContext(AuthenticationContext);
    const notificationContext = useContext(NotificationContext);
    const tenantContext = useContext(TenantContext);

    return (
        <ModalUserTokensUpdateInternal authenticationContext={authenticationContext} notificationContext={notificationContext} tenantContext={tenantContext} {...props}/>
    )
}